import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { LmsButton, LmsInput } from '@shared/ui'
import { FlipbookIcon } from './flipbook-icons'

interface FlipbookPageJumpProps {
  readonly displayPage: number
  readonly isPageLoading: boolean
  readonly onNavigateToPage: (page: number) => void
  readonly pageCount: number
  readonly visiblePageNumbers: readonly number[]
}

export function FlipbookPageJump({
  displayPage,
  isPageLoading,
  onNavigateToPage,
  pageCount,
  visiblePageNumbers,
}: FlipbookPageJumpProps) {
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const isComposingRef = useRef(false)
  const submitLockedRef = useRef(false)
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The existing navigation hook commits the page before loading its resources.
  // Keep duplicate events in that same commit from submitting another jump.
  useEffect(() => {
    submitLockedRef.current = false
  }, [displayPage, isPageLoading])

  const handleCancel = () => {
    setDraft(null)
    setError(null)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isPageLoading || isComposingRef.current || submitLockedRef.current) {
      return
    }

    const value = (draft ?? String(displayPage)).trim()
    const targetPage = Number(value)
    if (!value || !/^[0-9]+$/.test(value) || !Number.isSafeInteger(targetPage) || targetPage < 1 || targetPage > pageCount) {
      setDraft(draft ?? String(displayPage))
      setError(value ? `Nhập số nguyên từ 1 đến ${pageCount}.` : 'Nhập số trang.')
      inputRef.current?.focus()
      return
    }

    handleCancel()
    if (visiblePageNumbers.includes(targetPage)) {
      return
    }

    submitLockedRef.current = true
    onNavigateToPage(targetPage)
  }

  return (
    <form
      aria-busy={isPageLoading}
      aria-label="Di chuyển đến trang PDF"
      className="flipbookPageJump"
      noValidate
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          handleCancel()
        }
      }}
      onSubmit={handleSubmit}
    >
      <label className="sr-only" htmlFor={`${id}-page`}>Trang</label>
      <div className="flipbookPageJumpControl">
      <LmsInput
        aria-describedby={`${id}-total${error ? ` ${id}-error` : ''}`}
        aria-invalid={Boolean(error)}
        autoComplete="off"
        className="flipbookPageJumpInput"
        disabled={isPageLoading}
        fullWidth={false}
        id={`${id}-page`}
        inputMode="numeric"
        onChange={(event) => {
          setDraft(event.target.value)
          setError(null)
        }}
        onCompositionEnd={() => { isComposingRef.current = false }}
        onCompositionStart={() => { isComposingRef.current = true }}
        onFocus={(event) => {
          setDraft((current) => current ?? String(displayPage))
          event.currentTarget.select()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (isComposingRef.current || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229)) {
            event.preventDefault()
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
          }
        }}
        ref={inputRef}
        type="text"
        value={draft ?? String(displayPage)}
      />
      <span className="flipbookPageJumpTotal" id={`${id}-total`}>/ {pageCount}</span>
      <LmsButton
        aria-label="Đến trang"
        className="flipbookPageJumpSubmit"
        disabled={isPageLoading}
        onPointerDown={(event) => {
          // Retain input focus/draft on browsers where pointer clicks do not focus buttons.
          event.preventDefault()
        }}
        type="submit"
        title="Đến trang · Enter"
        size="icon"
        variant="ghost"
      >
        <FlipbookIcon className="size-4" name="arrow-right" />
      </LmsButton>
      </div>
      {error ? <p className="flipbookPageJumpError" id={`${id}-error`} role="alert">{error}</p> : null}
    </form>
  )
}
